import React, { useState, SyntheticEvent } from 'react';

import { Typography, Button, Dialog, DialogTitle } from '@mui/material';

import { useForm, FormProvider } from 'react-hook-form';

import { ContentToggler } from './Container';

import { AuthAlternative, Error, LinkStyled } from './styles';
import { DialogContentStyled, DialogActionsStyled } from 'elements/mismosh';

import { signin } from 'components/authorization/cognitoUtils';
import { Phone, e164Formatter } from 'components/phone';
import { Password } from './password';
import { useAuthContext } from 'components/authorization/AuthProvider';
import { AlertDialog } from 'components/Alert';

interface Props {
  switchToClose: ContentToggler;
  switchToForgotPassword: ContentToggler;
  switchToVerificationCode: ContentToggler;
}

type FormData = {
  phone: string;
  password: string;
};

const defaultValues: FormData = {
  phone: '',
  password: '',
};

export const SignIn: React.FC<Props> = ({
  switchToForgotPassword,
  switchToClose,
  switchToVerificationCode,
}) => {
  const methods = useForm<FormData>({ defaultValues });
  const [error, setError] = useState('');
  const [phone, setPhone] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [showUserNotConfirmed, setShowUserNotConfirmed] = useState(false);
  const { logout, setUsername } = useAuthContext();

  const handleClose = () => {
    switchToClose();
  };

  const handleGotoForgotPassword = () => {
    handleClose();
    switchToForgotPassword();
  };

  const handleGotoVerificationCode = () => {
    handleClose();
    setUsername(phone);
    switchToVerificationCode();
  };

  const handleUserNotConfirmedResponse = (response: 0 | 1) => {
    setShowUserNotConfirmed(false);
    if (response == 0) {
      handleGotoVerificationCode();
    }
  };

  const onSubmit = async ({ phone, password }: FormData, e: SyntheticEvent) => {
    e.preventDefault();
    setLoading(true);
    await logout(false); // Since they submitted, be safe and blow alway all existing
    const e164 = e164Formatter(phone);
    try {
      await signin(e164, password);
      setError(undefined);
      handleClose();
    } catch (err) {
      if (err.code == 'UserNotConfirmedException') {
        setPhone(e164);
        setShowUserNotConfirmed(true);
      } else {
        const message =
          err.message?.indexOf('PreTokenGeneration failed') > -1
            ? 'Pretoken generation failed'
            : err.message ?? 'Failed with no message';
        setError(message);
        console.log('error...: ', err);
      }
    }
    setLoading(false);
  };

  return (
    <React.Fragment>
      <Dialog
        open={true}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="signin-modal">Sign In</DialogTitle>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <DialogContentStyled sx={{ marginBottom: -2 }}>
              <Phone />
              <Password label="Password" />
              <Error>{error ? error : '\u00a0'}</Error>
            </DialogContentStyled>
            <DialogActionsStyled>
              <Button
                disabled={loading}
                name="signin-submit"
                variant="contained"
                type="submit"
              >
                Sign In
              </Button>
            </DialogActionsStyled>
            <AuthAlternative>
              <Typography variant="caption">Forgot password?</Typography>
              <LinkStyled onClick={handleGotoForgotPassword}>
                Reset password
              </LinkStyled>
            </AuthAlternative>
          </form>
        </FormProvider>
      </Dialog>
      <AlertDialog
        content="Your phone number has not been confirmed by SMS code entry"
        title="Phone Not Confirmed"
        option0Text="Enter or Resend Code"
        option1Text="Use a different phone number"
        sendUserClick={handleUserNotConfirmedResponse}
        open={showUserNotConfirmed}
      />
    </React.Fragment>
  );
};
